<template>
  <span class="event-date-start-purchase-btn no-print">
    <!-- Access Code Dialog -->
    <access-code-dialog
      v-model="showAccessCodeDialog"
      :loading="accessCodeLoading"
      :error-message="accessCodeError"
      @submit="onAccessCodeSubmit"
      @cancel="onAccessCodeCancel"
    />

    <!-- Dialog for custom sold-out message -->
    <v-dialog
      v-if="showSoldOutButton"
      v-model="showCustomSoldOutMessage"
      max-width="640"
    >
      <v-card>
        <v-card-title>{{ buttonTextSoldOut }}</v-card-title>
        <v-card-text>
          {{ eventDate.eventCustomSoldOutText }}
        </v-card-text>
        <v-card-actions class="flex justify-end">
          <v-btn
            color="error"
            variant="text"
            @click="closeCustomSoldOutMessage"
            :density="buttonDensity"
            :size="buttonSize"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Button that either navigates or opens dialog -->
    <v-btn
      v-if="showSoldOutButton"
      color="blue"
      variant="outlined"
      @click="handleSoldOutButtonClick"
      :prepend-icon="moreOptionsIcon"
      :density="buttonDensity"
      :size="buttonSize"
    >
      {{ buttonTextSoldOut }}
    </v-btn>

    <!-- Start Purchase Button -->
    <v-btn
      v-if="!eventDate.unpurchasableReason"
      v-bind="$attrs"
      @click="handleStartPurchaseButtonClick"
      :disabled="purchaseService.initiatePurchase.isLoading"
      :loading="isStartPurchaseLoading"
      variant="elevated"
      :density="buttonDensity"
      :size="buttonSize"
    >
      <slot>
        <v-icon start theme="dark">{{ selectTicketsIcon }}</v-icon>
        {{ buttonTextSelectTickets }}
      </slot>
    </v-btn>
  </span>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from "vue";
import { EventDateDto } from "@/models.g";
import { PublicPurchaseServiceViewModel } from "@/viewmodels.g";
import { Density } from "vuetify/lib/composables/density";
import usePurchase from "@common/composables/usePurchase";
import { ProvideInjectConsts } from "@common/provideInjectConsts";
import AccessCodeDialog from "@common/event/access-code-dialog.vue";
import useStaticData from "@common/composables/useStaticData";

const props = withDefaults(
  defineProps<{
    eventDate: EventDateDto;
    buttonSize?: string;
    buttonDensity?: Density;
  }>(),
  {
    buttonDensity: "default",
  },
);

const { isPublicUser } = useStaticData();

const showErrorSnackBar = inject(ProvideInjectConsts.ShowErrorSnackBar) as (
  message: string,
) => void;

const purchase = usePurchase();
const startPurchase = purchase.startPurchase;
const startSeriesPurchase = purchase.startSeriesPurchase;
const initiatePurchase = purchase.initiatePurchase;
const initiateSeriesPurchase = purchase.initiateSeriesPurchase;

// Purchase service singleton
const purchaseService = new PublicPurchaseServiceViewModel();

const showCustomSoldOutMessage = ref(false);

const moreOptionsIcon = ref("fal fa-option");
const buttonTextMoreOptions = ref("More Options");
const selectTicketsIcon = ref("fal fa-money-bill-wave");
const buttonTextSelectTickets = ref("Select Tickets");
const moreOptionsUrl = ref("");
const selectTicketsUrl = ref("");
const showSoldOutButton = ref(false);
const isTenantCustomMoreOptionsButtonEnabled = ref(false);
const buttonTextSoldOut = ref("Sold Out");

// Access code dialog state
const showAccessCodeDialog = ref(false);
const accessCodeLoading = ref(false);
const accessCodeError = ref<string | null>(null);

onMounted(() => {
  // Handle custom button text and icons if enabled
  if (
    props.eventDate.soldOut &&
    props.eventDate.eventCustomSoldOutText &&
    props.eventDate.unpurchasableReason
  ) {
    showSoldOutButton.value = true;
  }

  if (props.eventDate.isTenantCustomMoreOptionsButtonEnabled) {
    isTenantCustomMoreOptionsButtonEnabled.value = true;
    if (props.eventDate.customMoreOptionsButtonText) {
      buttonTextSoldOut.value = props.eventDate.customMoreOptionsButtonText;
    }
  }

  if (props.eventDate.customMoreOptionsIcon) {
    moreOptionsIcon.value = props.eventDate.customMoreOptionsIcon;
  }
  if (props.eventDate.customMoreOptionsButtonText) {
    buttonTextMoreOptions.value = props.eventDate.customMoreOptionsButtonText;
  }
  if (props.eventDate.customSelectTicketsIcon) {
    selectTicketsIcon.value = props.eventDate.customSelectTicketsIcon;
  }
  if (props.eventDate.customSelectTicketsButtonText) {
    buttonTextSelectTickets.value =
      props.eventDate.customSelectTicketsButtonText;
  }

  if (props.eventDate.customMoreOptionsUrl) {
    moreOptionsUrl.value = props.eventDate.customMoreOptionsUrl;
  }

  if (props.eventDate.customSelectTicketsUrl) {
    selectTicketsUrl.value = props.eventDate.customSelectTicketsUrl;
  }
});

const isStartPurchaseLoading = computed(() => {
  return (
    purchaseService.initiatePurchase.isLoading &&
    purchaseService.initiatePurchase.args.eventDateId === props.eventDate.id
  );
});

const openExtWindow = (url: string) => {
  if (url) {
    window.open(url, "_blank");
  }
};

const handleStartPurchaseButtonClick = async () => {
  // Check if this event requires an access code (property only exists in public app models)
  if (isPublicUser && props.eventDate.requiresAccessCode) {
    accessCodeError.value = null;
    showAccessCodeDialog.value = true;
    return;
  }

  if (props.eventDate.isSeries && props.eventDate.seriesId) {
    try {
      if (!(await startSeriesPurchase(props.eventDate.seriesId))) {
        showErrorSnackBar(seriesPurchaseError());
      }
    } catch {
      showErrorSnackBar(seriesPurchaseError());
    }
    return;
  }

  try {
    await startPurchase(selectTicketsUrl.value, props.eventDate.id);
  } catch {
    showErrorSnackBar(
      initiatePurchase.message ||
        "An error occurred when selecting the event. Please try again.",
    );
  }
};

function seriesPurchaseError(): string {
  return (
    initiateSeriesPurchase.message ||
    "An error occurred when selecting the event. Please try again."
  );
}

const onAccessCodeSubmit = async (accessCode: string) => {
  accessCodeLoading.value = true;
  accessCodeError.value = null;

  try {
    if (props.eventDate.isSeries && props.eventDate.seriesId) {
      if (!(await startSeriesPurchase(props.eventDate.seriesId, accessCode))) {
        accessCodeError.value =
          initiateSeriesPurchase.message ||
          "Invalid access code. Please try again.";
        return;
      }
    } else {
      await startPurchase(
        selectTicketsUrl.value,
        props.eventDate.id,
        accessCode,
      );
    }
    // If we get here without error, the code was valid
    showAccessCodeDialog.value = false;
  } catch {
    accessCodeError.value =
      initiatePurchase.message || "Invalid access code. Please try again.";
  } finally {
    accessCodeLoading.value = false;
  }
};

const onAccessCodeCancel = () => {
  accessCodeError.value = null;
};

const closeCustomSoldOutMessage = () => {
  showCustomSoldOutMessage.value = false;
};

const handleSoldOutButtonClick = () => {
  if (props.eventDate.customMoreOptionsUrl) {
    openExtWindow(props.eventDate.customMoreOptionsUrl);
  } else {
    showCustomSoldOutMessage.value = true;
  }
};
</script>

<style lang="scss">
.event-date-start-purchase-btn {
  .v-icon.fa-money-bill-wave {
    margin-right: 12px;
  }
}
</style>
